import { type CSSProperties, type ReactElement } from "react";
import {
    Check,
    CheckSquare,
    Inbox,
    type LucideIcon,
    MessageSquare,
    RotateCcw,
    StickyNote,
} from "lucide-react";
import { color, layout, radius, space } from "@tokens/index";
import { Button, Text } from "@primitives/index";
import { activityTypeLabel } from "../../experience-shell/workflows/timeline";

/**
 * One row in the work-item filter list. A narrow projection: an id, a type
 * (for the glyph) and a pre-built label.
 */
export interface TimelineFilterWorkItem {
    readonly id: string;
    readonly type: string;
    readonly label: string;
}

export interface WorkflowTimelineFiltersProps {
    /** Work items that have audit history, for the "filter by item" list. */
    readonly workItems: readonly TimelineFilterWorkItem[];
    /** Selected work-item id, or null for "all activity". */
    readonly selectedWorkItemId: string | null;
    /** Select an item, or null to clear back to all activity. */
    readonly onSelectWorkItem: (id: string | null) => void;
    /** Activity types present in the feed, recency-ordered. */
    readonly activityTypes: readonly string[];
    /** Types the user has hidden. */
    readonly hiddenActivityTypes: ReadonlySet<string>;
    /** Toggle a single activity type's visibility. */
    readonly onToggleActivityType: (type: string) => void;
    /** Restore all activity types to visible. */
    readonly onResetActivityTypes: () => void;
}

const rootStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space[5],
    minWidth: 0,
};

const sectionStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space[2],
    minWidth: 0,
};

const sectionHeaderStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space[2],
    paddingInline: space[2],
    minHeight: space[6],
};

const listStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space[1],
    listStyle: "none",
    margin: 0,
    padding: 0,
};

function rowButtonStyle(active: boolean, dimmed: boolean): CSSProperties {
    return {
        appearance: "none",
        border: "none",
        borderLeft: `3px solid ${active ? color.accent.primary : "transparent"}`,
        cursor: "pointer",
        textAlign: "left",
        width: "100%",
        minHeight: layout.hitArea,
        display: "flex",
        alignItems: "center",
        gap: space[2],
        paddingInline: space[2],
        paddingBlock: space[2],
        borderRadius: radius.md,
        background: active ? color.bg.selected : "transparent",
        color: dimmed ? color.fg.subtle : color.fg.default,
        minWidth: 0,
    };
}

function switchBoxStyle(shown: boolean): CSSProperties {
    return {
        width: "16px",
        height: "16px",
        borderRadius: radius.sm,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: shown ? color.accent.primary : "transparent",
        border: shown ? "none" : `1.5px solid ${color.border.strong}`,
        color: color.accent.onPrimary,
    };
}

function workItemIcon(type: string): LucideIcon {
    switch (type) {
        case "task":
            return CheckSquare;
        case "note":
            return StickyNote;
        case "message":
            return MessageSquare;
        default:
            return Inbox;
    }
}

/**
 * Timeline filter groups: a work-item selector and a per-type show/hide
 * list. Presentation-agnostic: the timeline shell decides whether the
 * groups sit in a side rail or in a bottom sheet. Selection state and the
 * apply-to-feed logic are owned by the orchestrator, which mirrors them
 * into the URL so a filtered view deep-links.
 */
export function WorkflowTimelineFilters({
    workItems,
    selectedWorkItemId,
    onSelectWorkItem,
    activityTypes,
    hiddenActivityTypes,
    onToggleActivityType,
    onResetActivityTypes,
}: WorkflowTimelineFiltersProps): ReactElement {
    return (
        <div style={rootStyle}>
            <section style={sectionStyle} aria-label="Filter by work item">
                <div style={sectionHeaderStyle}>
                    <Text role="caps" size="xs" tone="muted">
                        Work item
                    </Text>
                </div>
                <ul style={listStyle}>
                    <li>
                        <button
                            type="button"
                            style={rowButtonStyle(selectedWorkItemId === null, false)}
                            aria-pressed={selectedWorkItemId === null}
                            onClick={() => onSelectWorkItem(null)}
                        >
                            <Inbox
                                size={15}
                                strokeWidth={2.25}
                                aria-hidden="true"
                                style={{ flexShrink: 0 }}
                            />
                            <Text role="bodySm" truncate>
                                All activity
                            </Text>
                        </button>
                    </li>
                    {workItems.map((item) => {
                        const Icon = workItemIcon(item.type);
                        const active = selectedWorkItemId === item.id;
                        return (
                            <li key={item.id}>
                                <button
                                    type="button"
                                    style={rowButtonStyle(active, false)}
                                    aria-pressed={active}
                                    onClick={() => onSelectWorkItem(active ? null : item.id)}
                                    title={item.label}
                                >
                                    <Icon
                                        size={15}
                                        strokeWidth={2.25}
                                        aria-hidden="true"
                                        style={{ flexShrink: 0, color: color.fg.muted }}
                                    />
                                    <Text role="bodySm" truncate>
                                        {item.label}
                                    </Text>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </section>

            {activityTypes.length > 0 ? (
                <section style={sectionStyle} aria-label="Filter by activity type">
                    <div style={sectionHeaderStyle}>
                        <Text role="caps" size="xs" tone="muted">
                            Activity type
                        </Text>
                        {hiddenActivityTypes.size > 0 ? (
                            <Button variant="ghost" size="sm" onClick={onResetActivityTypes}>
                                <RotateCcw size={12} strokeWidth={2.25} aria-hidden="true" />
                                Reset
                            </Button>
                        ) : null}
                    </div>
                    <ul style={listStyle}>
                        {activityTypes.map((type) => {
                            const shown = !hiddenActivityTypes.has(type);
                            return (
                                <li key={type}>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={shown}
                                        style={rowButtonStyle(false, !shown)}
                                        onClick={() => onToggleActivityType(type)}
                                    >
                                        <span aria-hidden="true" style={switchBoxStyle(shown)}>
                                            {shown ? <Check size={12} strokeWidth={3} /> : null}
                                        </span>
                                        <Text role="bodySm" truncate>
                                            {activityTypeLabel(type)}
                                        </Text>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ) : null}
        </div>
    );
}
